import React, { useState, useEffect, useMemo } from "react";
import classnames from "classnames";
import { FeatureListing } from "../../StarterTrial/FeatureListing";
import {
    PlanPrice,
    PlanPrivacy,
    PlanFrequencySelector
} from "../../StarterTrial/Credits/ChangeWorkspacePlanTableCredits";
import Button from "../../Button";
import { useStore } from "zustand";
import { useGlobalStore } from "../../../../../state/StoreContext";
import { fetchCheckoutUrlAndRedirect } from "../../../../../backendapi/workspaceApi";
import ComparisonTable from "./ComparisonTableCredits";
import {
    frequencies,
    getPlanPricing,
    getPlanData
} from "../../../../../clientServices/workspaceFeaturesCredits";
import { Chip } from "../../Chip";
import { isFreeOrTrialPlan, isTrialPlan } from "@functions/workspacePlans";
import { Plan } from "@functions/plans/types";
import { getPricingTablePlans } from "@functions/plans/utilities";
import { CancelPlanController } from "../../Billing/CancelPlan/CancelPlanController";
import { selfServePlanIsExpiring } from "@functions/services/stripe/stripeUtils";
import { usePaidPlanSubscription } from "../../../hooks/usePaidPlanSubscription";
import subscriptionsApi from "../../../../../backendapi/subscriptionsApi";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import { useTrial } from "../../../hooks/useTrial";
import { trialDurationLabel, getTrialIncludedPlanNames } from "@functions/plans/trialConstants";
import { TrialStartBanner } from "../../Trial/TrialStartBanner";

const PlanColumn = ({ children, className = "", recommended, recommendedText }) => {
    return (
        <div
            className={classnames(
                "relative flex h-full w-full flex-col gap-6 overflow-visible p-6 pt-7 md:h-auto 2xl:p-8",
                className,
                recommended && "recommendedPlan -my-2 md:-mx-2 md:-my-1"
            )}
        >
            {recommended && recommendedText && (
                <div className="recommendedTag">
                    <span className="recommendedText">{recommendedText}</span>
                </div>
            )}
            {children}
        </div>
    );
};

PlanColumn.PlanIntro = ({
    planIcon,
    planName,
    planDescription,
    planKey,
    frequency,
    setFrequency,
    recommended,
    className = "",
    isDelistedPlan = false,
    isCurrentPlan = false
}) => {
    return (
        <div className={classnames("flex flex-col gap-6", className)}>
            <div className="flex flex-col items-start gap-2 2xl:flex-row 2xl:items-center 2xl:justify-between">
                <div className="flex flex-row items-center gap-2 text-2xl font-semibold leading-9 text-gray-900">
                    <i
                        className={classnames(
                            planIcon,
                            recommended ? "text-yellow-500" : "text-brand"
                        )}
                    ></i>
                    <span className="whitespace-nowrap font-semibold">{planName}</span>
                </div>
                <Chip color="gray">{planDescription}</Chip>
            </div>
            <div className="flex flex-1 flex-col justify-between gap-6">
                {isDelistedPlan ? (
                    <div className="flex flex-col items-start gap-3 py-2">
                        <div className="flex items-center gap-2">
                            <span className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700">
                                Legacy Plan
                            </span>
                        </div>
                        <span className="text-sm font-normal leading-6 text-gray-500">
                            No longer available for purchase
                        </span>
                    </div>
                ) : (
                    <PlanPrice planType={planKey} frequency={frequency} />
                )}
                <div
                    className={classnames(
                        planKey === Plan.UbpBasic ||
                            planKey === Plan.UbpCore ||
                            planKey === Plan.UbpGrowth
                            ? "visible"
                            : "invisible"
                    )}
                >
                    {!isDelistedPlan && !isCurrentPlan && (
                        <PlanFrequencySelector
                            frequencyOptions={frequencies}
                            frequency={frequency}
                            setFrequency={setFrequency}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

PlanColumn.SpecialFeature = ({ icon, children }) => {
    return (
        <div className="flex flex-row items-center gap-2 self-stretch border-b-1 border-t-1 border-gray-200 py-6 text-sm text-primary">
            <i className={icon}></i>
            <div>{children}</div>
        </div>
    );
};

PlanColumn.FeatureList = ({ listTitle, listSubtitle, titleClassName = "", children }) => {
    return (
        <div className="flex flex-col gap-2">
            <span className={classnames("text-sm font-semibold text-primary", titleClassName)}>
                {listTitle}
            </span>
            {listSubtitle && <span className="text-sm text-primary">{listSubtitle}</span>}
            <div className="flex flex-col gap-2 pl-1">{children}</div>
        </div>
    );
};

const getCtaText = ({ planKey, planName, frequency, currentPlan, usage, isExpiring }) => {
    const ctaFrequency =
        planKey === Plan.UbpBasic || planKey === Plan.UbpCore
            ? frequency.labelWithoutDiscount + " "
            : null;
    if (planKey === Plan.Enterprise) {
        return "Contact Sales";
    } else if (currentPlan === planKey) {
        if (isExpiring) {
            return "Reactivate Plan";
        }
        return "Current Plan";
    } else if (planKey === Plan.Public && isFreeOrTrialPlan(usage)) {
        return "Switch to Public Plan";
    } else if (
        planKey === Plan.Public ||
        (planKey === Plan.UbpBasic &&
            (currentPlan === Plan.UbpGrowth || currentPlan === Plan.LegacyStarter) &&
            !isTrialPlan(usage)) ||
        (planKey === Plan.UbpCore && currentPlan === Plan.UbpGrowth && !isTrialPlan(usage))
    ) {
        return (
            <>
                <span className="hidden lg:inline">
                    Downgrade to {ctaFrequency}
                    {planName}
                </span>
                <span className="lg:hidden">Downgrade to {planName}</span>
            </>
        );
    }
    return (
        <>
            <span className="inline">Upgrade to {planName}</span>
        </>
    );
};

export const PlanButton = ({
    planDetails,
    planKey,
    usage,
    handlePlanDowngrade,
    handleSalesFormClick,
    currentPlan,
    frequency,
    isProcessing,
    handlePlanPurchase,
    isEligibleForTrial = false
}) => {
    const [loading, setLoading] = useState(false);
    const projectSelector = useProjectSelector();
    const planPricing = useMemo(() => getPlanPricing(projectSelector), [projectSelector]);

    const { subscription, fetchPaidPlanSubscription } = usePaidPlanSubscription();
    const isExpiring = selfServePlanIsExpiring({ plan: currentPlan, subscription });

    const handlePlanSelect = async () => {
        if (currentPlan === planKey && isExpiring) {
            try {
                setLoading(true);
                await subscriptionsApi.reactivatePaidPlanSubscription(usage?.id);
                await fetchPaidPlanSubscription({ refetch: true });
            } catch (error) {
                setLoading(false);
                swal.fire({ title: "Error", text: error.message, icon: "error", timer: 5000 });
            }
        } else if (planKey === Plan.Enterprise) {
            handleSalesFormClick();
        } else if (planKey === Plan.Public && isFreeOrTrialPlan(usage)) {
            try {
                handlePlanDowngrade(Plan.Public);
            } catch (error) {
                setLoading(false);
                swal.fire({ title: "Error", text: error.message, icon: "error", timer: 5000 });
            }
        } else if (
            planKey === Plan.Public ||
            (planKey === Plan.UbpBasic &&
                (currentPlan === Plan.UbpGrowth || currentPlan === Plan.LegacyStarter) &&
                !isTrialPlan(usage)) ||
            (planKey === Plan.UbpCore && currentPlan === Plan.UbpGrowth && !isTrialPlan(usage))
        ) {
            handlePlanDowngrade(planKey);
        } else {
            handlePlanPurchase(planPricing[planKey][frequency.interval]);
        }

        setLoading(false);
    };
    const shouldDemoteCTA =
        isEligibleForTrial && planDetails.ctaType === "upgrade" && planKey !== Plan.Enterprise;
    const buttonType = shouldDemoteCTA ? "secondary" : planDetails.ctaType;

    return (
        <Button
            type={buttonType}
            onClick={handlePlanSelect}
            loading={isProcessing === planKey || loading}
            disabled={currentPlan === planKey && !isExpiring}
            className="self-stretch"
        >
            {getCtaText({
                planKey,
                planName: planDetails.name,
                frequency,
                currentPlan,
                usage,
                isExpiring
            })}
        </Button>
    );
};

export const renderPlanColumn = ({
    planKey,
    planDetails,
    frequency,
    setFrequency,
    recommended = false,
    fsrc = "",
    alwaysShowPublic = false,
    getPlanButton
}) => {
    const {
        icon,
        name,
        description,
        highlightedFeatures,
        planLimits,
        hasCustomLimits,
        isDelistedPlan,
        isCurrentPlan
    } = planDetails;

    return (
        <PlanColumn
            key={planKey}
            className={classnames(
                planKey === Plan.Public && !alwaysShowPublic ? "hidden xl:flex" : "",
                recommended && "!m-0"
            )}
            recommended={!isDelistedPlan && (planDetails.recommended || recommended)}
            recommendedText={
                isDelistedPlan
                    ? undefined
                    : hasCustomLimits
                      ? "YOUR CURRENT LIMITS"
                      : planDetails.recommendedText
            }
        >
            <div className="flex flex-col gap-2">
                <PlanColumn.PlanIntro
                    planIcon={icon}
                    planName={name}
                    planDescription={description}
                    planKey={planKey}
                    frequency={frequency}
                    setFrequency={setFrequency}
                    recommended={planDetails.recommended || recommended}
                    className={classnames((planDetails.recommended || recommended) && "pt-0.5")}
                    isDelistedPlan={isDelistedPlan}
                    isCurrentPlan={isCurrentPlan}
                />
                {getPlanButton({ planKey, planDetails })}
            </div>
            <PlanPrivacy planKey={planKey} planDetails={planDetails} />
            {/* Plan Limits Section */}
            {planLimits && planLimits.length > 0 && (
                <div className="flex flex-col gap-2 border-b-1 border-gray-200 pb-4">
                    {planLimits.map((limit, index) => (
                        <FeatureListing
                            key={index}
                            boldText={
                                typeof limit.value === "number"
                                    ? limit.value.toLocaleString()
                                    : limit.value
                            }
                            text={limit.text}
                        />
                    ))}
                </div>
            )}
            {/* Highlighted Features Section */}
            <div className="flex flex-col gap-2">
                {highlightedFeatures.map((feature, index) => (
                    <FeatureListing
                        key={index}
                        {...feature}
                        highlight={feature.highlight?.includes(fsrc)}
                    />
                ))}
            </div>
        </PlanColumn>
    );
};

export function PlansComparisonCredits({
    fsrc = "",
    excludePublic = false,
    closeModal,
    successUrl
}) {
    const [fetchingCheckoutUrl, setFetchingCheckoutUrl] = useState(null);
    const projectSelector = useProjectSelector();
    const globalStore = useGlobalStore();
    const usage = useStore(globalStore, (state) => state.getUsage());
    const workspace_id = useStore(globalStore, (state) => state.getWorkspace()?.id);
    const uid = useStore(globalStore, (state) => state.user.uid);
    const { isEligibleForTrial, activateTrial, isActivating: isActivatingTrial } = useTrial();
    const handleActivateTrial = () => activateTrial({ funnelSource: fsrc, onSuccess: closeModal });

    useEffect(() => {
        window?.eventTracker?.event({
            type: "Workspace Plans Seen",
            data: {
                funnelSource: fsrc,
                workspaceId: workspace_id,
                uid: uid,
                currentPlan: usage?.plan
            }
        });
    }, []);

    function handlePlanSelected(planData) {
        window?.eventTracker?.event({
            type: "Clicked Paid Plan",
            data: {
                funnelSource: fsrc,
                clickedFrom: "workspace_plans",
                workspaceId: workspace_id,
                uid: uid,
                currentPlan: usage?.plan,
                planType: planData.planType
            }
        });
        handlePlanPurchase({
            priceId: planData.priceId,
            price: planData.totalPrice,
            interval: planData.interval,
            planType: planData.planType
        });
    }

    async function handlePlanPurchase({ priceId, price, interval, planType }) {
        let data = {
            workspace_id,
            uid,
            plan: planType,
            funnel_source: fsrc,
            priceId,
            price,
            interval
        };
        if (successUrl) {
            data.success_url = successUrl;
        }
        if (fetchingCheckoutUrl) {
            return;
        }
        setFetchingCheckoutUrl(planType);
        await fetchCheckoutUrlAndRedirect(data);
        setFetchingCheckoutUrl(null);
    }

    return (
        <PlansTable
            fsrc={fsrc}
            currentPlan={usage?.plan}
            usage={usage}
            isProcessing={fetchingCheckoutUrl || (isActivatingTrial ? Plan.UbpPremiumTrial : null)}
            handlePlanPurchase={handlePlanSelected}
            excludePublic={excludePublic}
            closeModal={closeModal}
            isEligibleForTrial={isEligibleForTrial}
            onActivateTrial={handleActivateTrial}
            isActivatingTrial={isActivatingTrial}
        />
    );
}

export function PlansTable({
    fsrc = "",
    currentPlan = "",
    usage,
    isProcessing,
    handlePlanPurchase,
    excludePublic,
    closeModal,
    isEligibleForTrial,
    onActivateTrial,
    isActivatingTrial
}: {
    fsrc: string;
    currentPlan: string;
    usage: any;
    isProcessing: string | null;
    handlePlanPurchase: (frequency: any) => void;
    excludePublic: boolean;
    closeModal: () => void;
    isEligibleForTrial: boolean;
    onActivateTrial: () => Promise<void>;
    isActivatingTrial: boolean;
}) {
    const [frequency, setFrequency] = useState(frequencies[1]);
    const [selectedDowngradePlan, setSelectedDowngradePlan] = useState(null);
    const projectSelector = useProjectSelector();
    const { subscription } = usePaidPlanSubscription();

    // Derive the actual billing interval for the current plan
    const currentPlanFrequency = useMemo(() => {
        if (subscription?.isAnnual) {
            return frequencies.find((f) => f.interval === "annually") || frequencies[1];
        } else if (subscription) {
            return frequencies.find((f) => f.interval === "monthly") || frequencies[0];
        }
        // No subscription means free plan, use the toggled frequency
        return frequency;
    }, [subscription, frequency]);

    const handleSalesFormClick = () => {
        window?.open("https://roboflow.com/sales", "_blank");
        window?.eventTracker?.event({
            type: "Clicked Sales form",
            data: {
                funnelSource: fsrc,
                clickedFrom: "workspace_plans",
                workspaceId: usage?.id,
                uid: window?.user?.uid,
                currentPlan: usage?.plan
            }
        });
    };

    const plansOnTable = getPricingTablePlans(currentPlan);
    const planData = getPlanData(
        currentPlan,
        frequency,
        plansOnTable as any,
        usage,
        projectSelector
    );
    const planDataExcludingPublic = getPlanData(
        currentPlan,
        frequency,
        getPricingTablePlans(currentPlan, [Plan.Public]) as any,
        usage,
        projectSelector
    );

    // Override current plan data with actual billing interval
    if (currentPlan && planData[currentPlan]) {
        const currentPlanDataWithActualInterval = getPlanData(
            currentPlan,
            currentPlanFrequency,
            [currentPlan] as any,
            usage,
            projectSelector
        )[currentPlan];

        if (currentPlanDataWithActualInterval) {
            planData[currentPlan] = {
                ...planData[currentPlan],
                ...currentPlanDataWithActualInterval,
                // Preserve the isCurrentPlan flag
                isCurrentPlan: true
            };
        }
    }

    if (currentPlan && planDataExcludingPublic[currentPlan]) {
        const currentPlanDataWithActualInterval = getPlanData(
            currentPlan,
            currentPlanFrequency,
            [currentPlan] as any,
            usage,
            projectSelector
        )[currentPlan];

        if (currentPlanDataWithActualInterval) {
            planDataExcludingPublic[currentPlan] = {
                ...planDataExcludingPublic[currentPlan],
                ...currentPlanDataWithActualInterval,
                // Preserve the isCurrentPlan flag
                isCurrentPlan: true
            };
        }
    }

    const PlanHighlights = () => {
        const frequencyToggle = (
            <PlanFrequencySelector
                frequencyOptions={frequencies}
                frequency={frequency}
                setFrequency={setFrequency}
            />
        );
        return (
            <div className="planHighlights">
                {/* Large screens */}
                <div className="relative mt-2 hidden flex-row gap-1 divide-x divide-y-0 divide-brand overflow-visible rounded-md border border-brand bg-white transition duration-200 lg:mt-7 lg:flex">
                    {Object.entries(planData)
                        .filter(([key]) => !excludePublic || key !== Plan.Public)
                        .map(([key, details]) =>
                            renderPlanColumn({
                                planKey: key,
                                planDetails: details,
                                frequency,
                                setFrequency,
                                fsrc,
                                getPlanButton: ({ planKey, planDetails }) => (
                                    <PlanButton
                                        planKey={planKey}
                                        planDetails={planDetails}
                                        usage={usage}
                                        handlePlanDowngrade={() =>
                                            setSelectedDowngradePlan(planKey)
                                        }
                                        handleSalesFormClick={handleSalesFormClick}
                                        currentPlan={currentPlan}
                                        frequency={frequency}
                                        isProcessing={isProcessing}
                                        handlePlanPurchase={handlePlanPurchase}
                                        isEligibleForTrial={isEligibleForTrial}
                                    />
                                )
                            })
                        )}
                </div>

                {/* Small screens */}
                <div className="relative flex w-full flex-1 flex-col items-start divide-y divide-brand overflow-visible rounded-lg border border-brand bg-white md:mt-2 md:flex-row md:items-stretch md:divide-x md:divide-y-0 lg:hidden">
                    {Object.entries(planDataExcludingPublic)
                        .filter(([key]) => key !== Plan.Public)
                        .map(([key, details]) =>
                            renderPlanColumn({
                                planKey: key,
                                planDetails: details,
                                frequency,
                                setFrequency,
                                fsrc,
                                getPlanButton: ({ planKey, planDetails }) => (
                                    <PlanButton
                                        planKey={planKey}
                                        planDetails={planDetails}
                                        usage={usage}
                                        handlePlanDowngrade={() =>
                                            setSelectedDowngradePlan(planKey)
                                        }
                                        handleSalesFormClick={handleSalesFormClick}
                                        currentPlan={currentPlan}
                                        frequency={frequency}
                                        isProcessing={isProcessing}
                                        handlePlanPurchase={handlePlanPurchase}
                                        isEligibleForTrial={isEligibleForTrial}
                                    />
                                )
                            })
                        )}
                </div>
            </div>
        );
    };

    if (selectedDowngradePlan) {
        return (
            <CancelPlanController
                usage={usage}
                isOpen={true}
                onClose={() => setSelectedDowngradePlan(null)}
                onCloseParentModal={closeModal}
                selectedDowngradePlan={selectedDowngradePlan}
                selectedFrequency={frequency}
                fsrc={fsrc}
            />
        );
    }

    return (
        <div className="plansTable">
            <div className="header">
                <h1 className="title">Roboflow Pricing</h1>
                <div className="flex items-center gap-2">
                    <Button type="secondary" onClick={handleSalesFormClick}>
                        Talk to Sales
                    </Button>
                </div>
            </div>

            {isEligibleForTrial && (
                <TrialStartBanner
                    title="Try Roboflow paid features for free"
                    description={`Get started with premium features included with the ${getTrialIncludedPlanNames().join(" and ")} for ${trialDurationLabel}.`}
                    onStartTrial={onActivateTrial}
                    isLoading={isActivatingTrial}
                    className="mt-4"
                />
            )}

            <PlanHighlights />
            <ComparisonTable
                currentPlan={currentPlan}
                frequency={frequency}
                usage={usage}
                excludePublic={excludePublic}
                handleSalesFormClick={handleSalesFormClick}
                fsrc={fsrc}
                onFrequencyChange={(interval) => {
                    const newFrequency = frequencies.find((f) => f.interval === interval);
                    setFrequency(newFrequency);
                }}
                getPlanButton={({ planKey, planDetails }) => (
                    <PlanButton
                        planKey={planKey}
                        planDetails={planDetails}
                        usage={usage}
                        handlePlanDowngrade={() => setSelectedDowngradePlan(planKey)}
                        handleSalesFormClick={handleSalesFormClick}
                        currentPlan={currentPlan}
                        frequency={frequency}
                        isProcessing={isProcessing}
                        handlePlanPurchase={handlePlanPurchase}
                        isEligibleForTrial={isEligibleForTrial}
                    />
                )}
            />
        </div>
    );
}
